// PartnerDetail › Kiosks › Card stock (docs/KIOSK_CARD_STOCK.md): the batches of anonymous cards ordered from the
// processor for this partner's kiosks, how many are still in stock and how many sold, and — for a QOR system admin —
// ordering a new batch and canceling one still being made. A batch is made in minutes: while one is, the table reads it
// again every 30 seconds (the API asks the processor at most once a minute). Card reference numbers are never shown:
// a card is found by typing its CRN, which is only sent to the API to be hashed.
import { newIdempotencyKey, shortDate } from '@qor/web-shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { kioskApi, type CardBatch, type KioskDevice, type ProgramSummary } from '../api';
import { useStaff } from '../components/RequireStaff';
import { confirmed } from '../components/ui';
import { US_STATES } from '../usStates';

const STATUS: Record<CardBatch['status'], [label: string, badge: string]> = {
  requested: ['Being made', 'qc-badge-muted'],
  completed: ['Ready', 'qc-badge-success'],
  failed: ['Failed', 'qc-badge-danger'],
  canceled: ['Canceled', 'qc-badge-muted'],
};

const text = (f: FormData, name: string) => String(f.get(name) ?? '').trim();

export function KioskCardStock({
  partner,
  locations,
  devices,
  after,
}: {
  partner: ProgramSummary;
  /** Where cards can go: the partner's locations (or the location itself when on one). */
  locations: ProgramSummary[];
  devices: KioskDevice[];
  after: (ok: boolean, message: string) => void;
}) {
  const client = useQueryClient();
  const { user, scope } = useStaff();
  const canOrder = scope.internal && user.role === 'SystemAdmin';
  const [busy, setBusy] = useState(false);
  const [orderKey, setOrderKey] = useState(newIdempotencyKey);
  const [found, setFound] = useState<string | null>(null);
  const batches = useQuery({
    queryKey: ['card-batches', partner.id],
    queryFn: () => kioskApi.batches(partner.id),
    refetchInterval: (q) => (q.state.data?.some((b) => b.status === 'requested') ? 30_000 : false),
  });
  // a batch being made is read one by one: that read is what asks the processor
  const making = (batches.data ?? []).filter((b) => b.status === 'requested');
  useQuery({
    queryKey: ['card-batches', partner.id, 'making', making.map((b) => b.id).join()],
    queryFn: async () => {
      for (const b of making) await kioskApi.batch(b.id);
      await client.invalidateQueries({ queryKey: ['card-batches', partner.id], exact: true });
      return making.length;
    },
    enabled: making.length > 0,
    refetchInterval: 30_000,
  });

  const targets = partner.parentProgramId !== null ? [partner] : [partner, ...locations];
  const nameOf = (id: string) => targets.find((p) => p.id === id)?.displayName ?? '—';
  const list = batches.data ?? [];
  const inStock = list.reduce((n, b) => n + b.inStock, 0);
  const sold = list.reduce((n, b) => n + b.sold, 0);

  async function order(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const f = new FormData(form);
    const quantity = Number(text(f, 'Quantity'));
    const programId = text(f, 'ProgramId') || partner.id;
    if (!confirmed(`Order ${quantity} cards for ${nameOf(programId)}? The processor makes and ships them.`)) return;
    setBusy(true);
    const r = await kioskApi.orderBatch(
      {
        programId,
        deviceId: text(f, 'DeviceId') || null,
        quantity,
        printedName: text(f, 'PrintedName') || null,
        shipTo: {
          line1: text(f, 'Line1'),
          line2: text(f, 'Line2') || null,
          city: text(f, 'City'),
          region: text(f, 'Region'),
          postalCode: text(f, 'PostalCode'),
        },
        contact: { name: text(f, 'ContactName'), phone: text(f, 'ContactPhone') },
      },
      orderKey,
    );
    setBusy(false);
    if (r.ok) {
      form.reset();
      setOrderKey(newIdempotencyKey());
    }
    after(r.ok, r.ok ? `${quantity} cards ordered: they are being made.` : r.error);
    await client.invalidateQueries({ queryKey: ['card-batches', partner.id] });
  }

  async function cancel(b: CardBatch) {
    if (!confirmed(`Cancel this batch of ${b.quantity} cards?`)) return;
    setBusy(true);
    const r = await kioskApi.cancelBatch(b.id);
    setBusy(false);
    after(r.ok, r.ok ? 'Batch canceled.' : r.error);
    await client.invalidateQueries({ queryKey: ['card-batches', partner.id] });
  }

  async function find(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const crn = text(new FormData(form), 'CardReference');
    form.reset(); // the CRN does not stay on screen
    const r = await kioskApi.cardByReference(crn);
    setFound(r.ok ? r.data.cardholderId : null);
    if (!r.ok) after(false, 'No card has this card reference number.');
  }

  return (
    <div className="qc-panel" style={{ marginTop: 20 }}>
      <div className="qc-panel-title">
        <h3>Card stock</h3>
        <span>
          Anonymous cards ordered for these kiosks. A kiosk sells a card by activating it with its card reference
          number, then loads it. In stock: <strong>{inStock}</strong> · Sold: <strong>{sold}</strong>
        </span>
      </div>

      {list.length === 0 ? (
        <p className="qc-empty">{batches.isPending ? 'Loading…' : 'No cards ordered yet.'}</p>
      ) : (
        <table className="qc-plain-table">
          <thead>
            <tr>
              <th>Ordered</th>
              <th>For</th>
              <th>Cards</th>
              <th>Status</th>
              <th>In stock</th>
              <th>Sold</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {list.map((b) => (
              <tr key={b.id}>
                <td>{shortDate(b.createdAt)}</td>
                <td>{nameOf(b.programId)}</td>
                <td>{b.quantity}</td>
                <td>
                  <span className={`qc-badge ${STATUS[b.status][1]}`}>{STATUS[b.status][0]}</span>
                  {b.failureReason && <small className="qc-field-hint"> {b.failureReason}</small>}
                  {b.cardRefWeak && (
                    <small className="qc-field-hint" style={{ display: 'block' }}>
                      <span className="qc-badge qc-badge-danger">Guessable CRNs</span> QBalance refuses these cards.
                    </small>
                  )}
                  {b.withoutReference > 0 && (
                    <small className="qc-field-hint" style={{ display: 'block' }}>
                      {b.withoutReference} card(s) without a card reference number: a kiosk cannot name them.
                    </small>
                  )}
                </td>
                <td>{b.inStock}</td>
                <td>{b.sold}</td>
                <td style={{ textAlign: 'right' }}>
                  {canOrder && b.status === 'requested' && (
                    <button type="button" className="qc-button-ghost" disabled={busy} onClick={() => void cancel(b)}>
                      Cancel
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <form onSubmit={(e) => void find(e)} className="qc-cardholder-filters" style={{ marginTop: 12 }}>
        <input
          name="CardReference"
          placeholder="Card reference number"
          aria-label="Card reference number"
          autoComplete="off"
          required
          maxLength={60}
        />
        <button type="submit" className="qc-button-ghost">
          Find card
        </button>
        {found && <Link to={`/cardholders/${found}`}>Open the card</Link>}
      </form>

      {canOrder && (
        <form onSubmit={(e) => void order(e)} className="qc-form-grid" style={{ marginTop: 16 }}>
          <div className="qc-form-section">
            <h4>Order cards</h4>
          </div>
          <label className="qc-field">
            <span>Number of cards</span>
            <input name="Quantity" type="number" min={1} max={1000} defaultValue={100} required />
          </label>
          <label className="qc-field">
            <span>For</span>
            {targets.length === 1 ? (
              <p className="qc-static-value">{partner.displayName}</p>
            ) : (
              <select name="ProgramId" defaultValue={locations[0]?.id ?? partner.id}>
                {targets.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.id === partner.id ? `${p.displayName} itself` : p.displayName}
                  </option>
                ))}
              </select>
            )}
            <small className="qc-field-hint">Only the kiosks standing there can sell these cards.</small>
          </label>
          <label className="qc-field">
            <span>Kiosk (optional)</span>
            <select name="DeviceId" defaultValue="">
              <option value="">Any kiosk there</option>
              {devices.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.code}
                </option>
              ))}
            </select>
          </label>
          <label className="qc-field">
            <span>Name printed on the cards</span>
            <input name="PrintedName" placeholder={partner.displayName} maxLength={21} />
          </label>
          <label className="qc-field">
            <span>Ship to: street</span>
            <input name="Line1" required maxLength={120} />
          </label>
          <label className="qc-field">
            <span>Suite, floor (optional)</span>
            <input name="Line2" maxLength={120} />
          </label>
          <label className="qc-field">
            <span>City</span>
            <input name="City" required maxLength={80} />
          </label>
          <label className="qc-field">
            <span>State</span>
            <select name="Region" required defaultValue="">
              <option value="" disabled>
                Choose…
              </option>
              {US_STATES.map(([code, stateName]) => (
                <option key={code} value={code}>
                  {stateName}
                </option>
              ))}
            </select>
          </label>
          <label className="qc-field">
            <span>ZIP</span>
            <input name="PostalCode" required pattern="\d{5}(-\d{4})?" inputMode="numeric" />
          </label>
          <label className="qc-field">
            <span>Contact name</span>
            <input name="ContactName" required maxLength={80} />
          </label>
          <label className="qc-field">
            <span>Contact phone</span>
            <input name="ContactPhone" required inputMode="tel" placeholder="650 111 4236" />
          </label>
          <div className="qc-form-actions">
            <button type="submit" className="qor-button qor-button-primary" disabled={busy}>
              Order cards
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
